'use client';

import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useState, useEffect, type ReactNode } from 'react';
import { motion } from 'framer-motion';

interface HeaderStep {
  label: string;
}

interface RedExperienceData {
  clientName: string | null;
  experienceTitle: string | null;
  currentStep: number;
  totalSteps: number;
  cobrowseElement?: ReactNode;
}

interface HeaderProps {
  showProgress?: boolean;
  steps?: Array<HeaderStep>;
  currentStep?: number;
}

function Header({
  showProgress: initialShowProgress = false,
  steps: initialSteps = [],
  currentStep: initialCurrentStep = 0,
}: HeaderProps) {
  const [showProgress, setShowProgress] = useState(initialShowProgress);
  const [steps, setSteps] = useState<Array<HeaderStep>>(initialSteps);
  const [currentStep, setCurrentStep] = useState(initialCurrentStep);
  const [headerTitle, setHeaderTitle] = useState<string | null>(null);
  const pathname = usePathname();
  
  // RED Experience specific state
  const [redExperience, setRedExperience] = useState<RedExperienceData | null>(null);

  useEffect(() => {
    const handleProgressUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setSteps(customEvent.detail.steps || []);
        setCurrentStep(customEvent.detail.currentStep || 0);
        setShowProgress(true);
        setHeaderTitle(null);
        setRedExperience(null);
      }
    };

    const handleHideProgress = (event: Event) => {
      const customEvent = event as CustomEvent;
      setShowProgress(false);
      setSteps([]);
      setCurrentStep(0);
      setHeaderTitle(customEvent.detail?.title || null);
      setRedExperience(null);
    };

    const handleRedExperienceUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setRedExperience({
          clientName: customEvent.detail.clientName || null,
          experienceTitle: customEvent.detail.experienceTitle || null,
          currentStep: customEvent.detail.currentStep || 1,
          totalSteps: customEvent.detail.totalSteps || 7,
        });
        setShowProgress(false);
        setHeaderTitle(null);
      }
    };

    const handleRedExperienceHide = () => {
      setRedExperience(null);
    };

    window.addEventListener('enrollment-progress', handleProgressUpdate);
    window.addEventListener('hide-enrollment-progress', handleHideProgress);
    window.addEventListener('red-experience-progress', handleRedExperienceUpdate);
    window.addEventListener('red-experience-hide', handleRedExperienceHide);
    return () => {
      window.removeEventListener('enrollment-progress', handleProgressUpdate);
      window.removeEventListener('hide-enrollment-progress', handleHideProgress);
      window.removeEventListener('red-experience-progress', handleRedExperienceUpdate);
      window.removeEventListener('red-experience-hide', handleRedExperienceHide);
    };
  }, []);

  // Hide header for rep UI (rep has its own RepNavHeader)
  if (pathname?.startsWith('/rep/') || pathname?.endsWith('/calculating')) {
    return null;
  }

  const progressPercentage = steps.length > 0 ? ((currentStep + 1) / steps.length) * 100 : 0;
  const currentStepLabel = steps[currentStep]?.label || '';

  // RED Experience header layout
  if (redExperience) {
    return (
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <div className="container mx-auto px-3 py-2.5 sm:px-4 sm:py-3 max-w-4xl">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Logo + Title + Client Name */}
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <Image
                src="/tr-logo-dark-r-mark.svg"
                alt="TaxRise"
                width={100}
                height={32}
                priority
                className="h-6 sm:h-7 w-auto shrink-0"
              />
              <div className="h-5 w-px bg-gray-300 shrink-0 hidden sm:block" />
              <div className="min-w-0 shrink">
                <h1 className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                  {redExperience.experienceTitle}
                </h1>
                {redExperience.clientName && (
                  <p className="text-xs sm:text-sm text-slate-500 truncate">
                    {redExperience.clientName}
                  </p>
                )}
              </div>
            </div>

            {/* Center: Step dots */}
            <div className="flex items-center gap-1.5">
              {Array.from({ length: redExperience.totalSteps }).map((_, i) => {
                const stepId = i + 1;
                const isCurrent = stepId === redExperience!.currentStep;
                const isCompleted = stepId < redExperience!.currentStep;

                return (
                  <motion.div
                    key={i}
                    className={`rounded-full transition-colors duration-300 ${
                      isCurrent
                        ? 'w-6 h-1.5 bg-blue-500'
                        : isCompleted
                          ? 'w-1.5 h-1.5 bg-blue-500/60'
                          : 'w-1.5 h-1.5 bg-slate-200'
                    }`}
                    layout
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                );
              })}
            </div>

            {/* Right: Cobrowse slot */}
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <div id="header-cobrowse-slot" />
            </div>
          </div>
        </div>

        {/* Shimmer line at bottom */}
        <div
          className="h-px w-full"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.08), transparent)',
          }}
        />
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 border-b bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
      <div className="container mx-auto px-4 py-3 max-w-4xl">
        <div className="flex items-center justify-between">
          {/* Left: Logo + Step Progress */}
          <div className="flex items-center gap-4">
            <Image
              src="/tr-logo-dark-r-mark.svg"
              alt="TaxRise"
              width={100}
              height={32}
              priority
              className="h-7 w-auto"
            />
            {showProgress && steps.length > 0 && (
              <>
                <div className="h-5 w-px bg-gray-300" />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    Step {currentStep + 1} of {steps.length}
                  </span>
                  {currentStepLabel && (
                    <span className="hidden sm:inline text-sm font-medium text-gray-700">
                      {currentStepLabel}
                    </span>
                  )}
                </div>
              </>
            )}
            {!showProgress && headerTitle && (
              <>
                <div className="h-5 w-px bg-gray-300" />
                <span className="text-sm font-medium text-gray-700">{headerTitle}</span>
              </>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {showProgress && steps.length > 0 && (
          <div className="mt-3">
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
